/**
 * 온습도로거 '설치 지점' 탭 — 기록의 주인은 기기가 아니라 지점이다 (2026-11-04).
 *
 *  · 지점 = 현장의 고정된 측정 위치(하우스 · 위치 · 높이). 기기는 기간을 두고 지점에 설치된다.
 *  · 기기가 고장 나 예비기로 바꿔도 지점의 시계열 · 시험 배정 · 엑셀은 교체 시각을 경계로 그대로 이어진다.
 *  · 교체는 한 동작(교체 시각 · 내리는 사유 · 병행 측정 확인)으로 기록한다 — 기록 없이 기기만 바꾸면 그 순간부터
 *    지점의 값이 끊긴다(서버는 '설치 이력 기간 안의 측정값' 만 지점 값으로 본다).
 *  · 시험 배정은 지점에 건다. 기기 배정(옛 방식)은 장치 대장에 호환용으로 남아 있다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Card, DatePicker, Drawer, Dropdown, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag,
  Timeline, Tooltip, Typography, message, Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, SwapOutlined, HistoryOutlined, LinkOutlined, ReloadOutlined, DeleteOutlined,
  StopOutlined, LineChartOutlined, MoreOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { thloggerAPI, masterAPI, scheduleAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { gray, state } from '../theme/tokens';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { fmtDate, fmtDateTime, DATE_FORMAT, DATETIME_FORMAT } from '../utils/date';
import { EmptyState } from './common';
import { BatteryBadge, SignalBadge } from './ThLoggerBadges';
import { CardStylePicker } from './ThLoggerOverview';

const { Text } = Typography;

export const END_REASONS = ['고장', '배터리', '교정', '정기 교체', '이동', '철거', '기타'];
export const START_REASONS = ['신규 설치', '교체', '교정 복귀', '이동'];

const LV: Record<string, { label: string; color: string; bg: string }> = {
  OK: { label: '수신 정상', color: state.successText, bg: state.successSoft },
  WARN: { label: '수신 지연', color: state.warningText, bg: state.warningSoft },
  CRIT: { label: '미수신', color: state.errorText, bg: state.errorSoft },
  NEW: { label: '수신 대기', color: gray[600], bg: gray[100] },
};
const lvTag = (lv?: string) => {
  const m = LV[lv || ''] || { label: '기기 없음', color: gray[600], bg: gray[100] };
  return <Tag style={{ margin: 0, color: m.color, background: m.bg, borderColor: m.bg, fontWeight: 700 }}>{m.label}</Tag>;
};
const err = (e: any, fb: string) => message.error(e?.response?.data?.detail || fb);

const ThLoggerPoints: React.FC<{ points: any[]; devices: any[]; loading: boolean; reload: () => void; onOpenData: (pointId: number) => void }> = ({ points, devices, loading, reload, onOpenData }) => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('thlogger', 'WRITE');
  const canDelete = hasPermission('thlogger', 'DELETE');
  const [search, setSearch] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);   // {} = 신규
  const [form] = Form.useForm();
  const [swapFor, setSwapFor] = useState<any | null>(null);
  const [swapForm] = Form.useForm();
  const [histFor, setHistFor] = useState<any | null>(null);
  const [hist, setHist] = useState<{ devices: any[]; assignments: any[] } | null>(null);
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [assignForm] = Form.useForm();
  const [schedOpts, setSchedOpts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { masterAPI.locations.list().then(r => setLocations(r.data || [])).catch(() => {}); }, []);

  const rows = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return points.filter(p => !kw || [p.code, p.name, p.position, p.current?.device_name, p.current?.imei, p.current_assignment?.test_number]
      .some(v => v && String(v).toLowerCase().includes(kw)));
  }, [points, search]);

  // 지금 어느 지점에도 설치되지 않은 활성 기기 = 예비기 후보
  const placed = useMemo(() => new Set(points.map(p => p.current?.device_id).filter(Boolean)), [points]);
  const spares = devices.filter(d => d.is_active && !placed.has(d.id));

  const openEdit = (p: any | null) => {
    setEditing(p || {});
    form.resetFields();
    form.setFieldsValue(p ? { ...p } : { sort_order: (points.length + 1) * 10 });
  };
  const submitEdit = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (editing?.id) await thloggerAPI.updatePoint(editing.id, v); else await thloggerAPI.createPoint(v);
      message.success(editing?.id ? '수정되었습니다.' : '등록되었습니다.');
      setEditing(null); reload();
    } catch (e) { err(e, '저장에 실패했습니다.'); } finally { setSaving(false); }
  };

  const openSwap = (p: any) => {
    setSwapFor(p);
    swapForm.resetFields();
    swapForm.setFieldsValue({ at: dayjs(), end_reason: p.current ? '고장' : undefined, start_reason: p.current ? '교체' : '신규 설치' });
  };
  const submitSwap = async (remove = false) => {
    const v = await swapForm.validateFields();
    if (!remove && !v.device_id) { message.warning('설치할 기기를 고르세요.'); return; }
    setSaving(true);
    try {
      await thloggerAPI.swapPoint(swapFor.id, {
        device_id: remove ? null : v.device_id, at: v.at.format('YYYY-MM-DDTHH:mm:00+09:00'),
        start_reason: v.start_reason, end_reason: remove ? (v.end_reason || '철거') : v.end_reason, check_note: v.check_note, note: v.note,
      });
      message.success(remove ? '철거를 기록했습니다.' : swapFor.current ? '교체를 기록했습니다. 지점 기록이 새 기기로 이어집니다.' : '설치를 기록했습니다.');
      setSwapFor(null); reload();
    } catch (e) { err(e, '기록에 실패했습니다.'); } finally { setSaving(false); }
  };

  const openHist = async (p: any) => {
    setHistFor(p); setHist(null);
    try { const { data } = await thloggerAPI.pointHistory(p.id); setHist(data); } catch (e) { err(e, '이력을 불러오지 못했습니다.'); }
  };

  const searchSchedules = (q: string) => {
    if (!q || q.length < 2) return;
    scheduleAPI.list({ search: q, page: 1, page_size: 30 }).then(r => {
      const items = r.data?.items || r.data || [];
      setSchedOpts(items.map((s: any) => ({ value: s.id, label: `${s.test_number} · ${s.title || ''}` })));
    }).catch(() => {});
  };
  const submitAssign = async () => {
    const v = await assignForm.validateFields();
    if (!v.schedule_id && !v.test_number) { message.warning('시험일정이나 시험번호를 입력하세요.'); return; }
    setSaving(true);
    try {
      await thloggerAPI.createPointAssignment(assignFor.id, {
        schedule_id: v.schedule_id, test_number: v.test_number, valid_from: v.valid_from.format('YYYY-MM-DDT00:00:00+09:00'),
        valid_to: v.valid_to ? v.valid_to.format('YYYY-MM-DDT23:59:59+09:00') : null, note: v.note,
      });
      message.success('배정되었습니다.');
      setAssignFor(null); reload();
    } catch (e) { err(e, '배정에 실패했습니다.'); } finally { setSaving(false); }
  };

  // 열 수를 줄였다(2026-11-04 검토: 1366 폭에서 가로 346px 넘침) — 설치 위치 · 현황 카드 → 지점 칸, 상태 · 교체 횟수 → 현재 기기 칸,
  // 시험 배정 · 삭제 → 작업의 '더보기'. 열 폭 합 ≈ 900px
  const more = (p: any) => [
    ...(canWrite ? [{ key: 'assign', icon: <LinkOutlined />, label: '시험 배정', onClick: () => { setAssignFor(p); assignForm.resetFields(); assignForm.setFieldsValue({ valid_from: dayjs() }); } }] : []),
    ...(canDelete ? [{ key: 'del', icon: <DeleteOutlined />, danger: true, label: '삭제 · 폐지', onClick: () => Modal.confirm({
      title: `${p.code} 지점을 삭제할까요?`, content: '설치 이력이 있으면 지우지 않고 폐지(비활성)로 남깁니다.', okText: '삭제', cancelText: '취소', okButtonProps: { danger: true },
      onOk: async () => { try { const { data } = await thloggerAPI.deletePoint(p.id); message.success(data?.deactivated ? '폐지(비활성)로 남겼습니다.' : '삭제되었습니다.'); reload(); } catch (e) { err(e, '삭제에 실패했습니다.'); } },
    }) }] : []),
  ];
  const columns: any[] = [
    { title: '지점 · 설치 위치', key: 'code', width: 250, render: (_: any, p: any) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <CardStylePicker point={p} canWrite={canWrite} onSaved={reload} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{p.code}</b>{!p.is_active && <Tag style={{ margin: 0 }}>폐지</Tag>}
            <span style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.name}>{p.name}</span>
          </div>
          <div style={{ fontSize: 11.5, color: gray[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.position || ''}>
            {p.position || '설치 위치 미기재'}</div>
        </div>
      </div>) },
    { title: '현재 기기', key: 'dev', width: 210, render: (_: any, p: any) => p.current
      ? <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><b style={{ fontSize: 12.5 }}>{p.current.device_name}</b>{lvTag(p.device?.level)}</div>
          <Tooltip title={`IMEI ${p.current.imei} · 설치 ${fmtDateTime(p.current.valid_from)}`}>
            <div style={{ fontSize: 11.5, color: gray[500], whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {dayjs(p.current.valid_from).format('YYYY-MM-DD')}~{(p.history_count || 0) > 1 ? ` · 교체 ${p.history_count - 1}회` : ''}</div>
          </Tooltip>
        </div>
      : <div><Tag color="orange" style={{ margin: 0 }}>기기 없음</Tag><div style={{ fontSize: 11.5, color: '#b45309' }}>기록 중단</div></div> },
    { title: '최근 측정', key: 'last', width: 112, render: (_: any, p: any) => p.device?.last_reading_ts
      ? <div style={{ whiteSpace: 'nowrap' }}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{Number(p.device.last_temp_c).toFixed(1)}℃ · {Math.round(p.device.last_humidity)}%</span>
          <div style={{ fontSize: 11.5, color: gray[400] }}>{dayjs(p.device.last_reading_ts).format('MM-DD HH:mm')}</div></div> : <Text type="secondary">-</Text> },
    { title: '배터리 · 신호', key: 'bs', width: 112, render: (_: any, p: any) => p.device
      ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}><BatteryBadge v={p.device.last_battery_v} compact /><SignalBadge dbm={p.device.last_signal_dbm} compact /></div> : '-' },
    { title: '배정 시험', key: 'asg', width: 112, render: (_: any, p: any) => p.current_assignment
      ? <Tooltip title={p.current_assignment.schedule_title}><Tag color="geekblue" style={{ margin: 0 }}>{p.current_assignment.test_number}</Tag></Tooltip>
      : <Text type="secondary" style={{ fontSize: 12 }}>미배정</Text> },
    { title: '작업', key: 'act', width: 150, fixed: 'right' as const, render: (_: any, p: any) => (
      <Space size={2}>
        {canWrite && <Tooltip title={p.current ? '기기 교체 · 철거' : '기기 설치'}><Button type="text" size="small" icon={<SwapOutlined />} onClick={() => openSwap(p)} /></Tooltip>}
        <Tooltip title="설치 · 배정 이력"><Button type="text" size="small" icon={<HistoryOutlined />} onClick={() => openHist(p)} /></Tooltip>
        <Tooltip title="측정 데이터"><Button type="text" size="small" icon={<LineChartOutlined />} onClick={() => onOpenData(p.id)} /></Tooltip>
        {canWrite && <Tooltip title="수정"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(p)} /></Tooltip>}
        {more(p).length > 0 && <Dropdown trigger={['click']} menu={{ items: more(p) }}><Button type="text" size="small" icon={<MoreOutlined />} aria-label="더보기" /></Dropdown>}
      </Space>) },
  ];

  const noDevice = points.filter(p => p.is_active && !p.current).length;

  return (
    <div ref={cardWrapRef}>
      <Card bodyStyle={{ padding: '10px 14px 0' }}
        title={<Space><span>설치 지점</span><Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>기록은 지점에 이어진다 — 기기를 바꿀 때는 '교체'로 기록</Text></Space>}
        extra={<Space size={8}>
          {noDevice > 0 && <Tag color="orange" style={{ margin: 0 }}>기기 없는 지점 {noDevice}</Tag>}
          <Tag style={{ margin: 0 }}>예비기 {spares.length}대</Tag>
          <Input.Search size="small" allowClear placeholder="지점·기기·시험번호" style={{ width: 200 }} onSearch={setSearch} />
          {canWrite && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>지점 등록</Button>}
          <Tooltip title="새로고침"><Button size="small" icon={<ReloadOutlined />} onClick={reload} /></Tooltip>
        </Space>}>
        <div ref={tableWrapRef} className="fit-table">
          <Table dataSource={rows} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 'max-content', y: bodyHeight }}
            locale={{ emptyText: <EmptyState title="등록된 지점이 없습니다" description="하우스의 측정 위치를 지점으로 등록하고 기기를 설치하세요." size="sm" /> }}
            pagination={{ pageSize: 50, size: 'small', showTotal: (t: number, r: [number, number]) => `${r[0]}–${r[1]} / 총 ${t}건`, hideOnSinglePage: false }} />
        </div>
      </Card>

      {/* 지점 등록 · 수정 */}
      <Modal open={!!editing} title={editing?.id ? `지점 수정 — ${editing.code}` : '지점 등록'} onCancel={() => setEditing(null)} onOk={submitEdit}
        okText="저장" cancelText="취소" confirmLoading={saving} destroyOnClose width={560}>
        <Form form={form} layout="vertical" size="small">
          <Space size={8} style={{ display: 'flex' }} align="start">
            <Form.Item name="code" label="지점 코드" rules={[{ required: true }]} extra="현장 표지와 같게 (예: H01-1)"><Input style={{ width: 140 }} /></Form.Item>
            <Form.Item name="name" label="지점 이름" rules={[{ required: true }]} style={{ flex: 1 }}><Input placeholder="1번 하우스 중앙" /></Form.Item>
          </Space>
          <Space size={8} style={{ display: 'flex' }} align="start">
            <Form.Item name="location_id" label="시험장소" style={{ flex: 1 }}>
              <Select allowClear showSearch optionFilterProp="label" options={locations.map((l: any) => ({ value: l.id, label: l.full_name }))} /></Form.Item>
          </Space>
          <Form.Item name="position" label="설치 위치 · 높이 · 차폐" extra="같은 지점에 놓아야 기기가 바뀌어도 값이 이어진다 — 위치를 옮기면 새 지점으로 등록">
            <Input placeholder="중앙 통로 · 작물 생장점 높이 1.2m · 복사 차폐 덮개" /></Form.Item>
          <Space size={8} style={{ display: 'flex' }} align="start">
            <Form.Item name="sort_order" label="표시 순서"><InputNumber style={{ width: 100 }} /></Form.Item>
            {editing?.id && <Form.Item name="is_active" label="사용" valuePropName="checked"><Switch /></Form.Item>}
          </Space>
          <Form.Item name="note" label="비고"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* 기기 설치 · 교체 · 철거 */}
      <Modal open={!!swapFor} title={swapFor ? `${swapFor.current ? '기기 교체' : '기기 설치'} — ${swapFor.code} ${swapFor.name}` : ''}
        onCancel={() => setSwapFor(null)} destroyOnClose width={600}
        footer={<Space>
          {swapFor?.current && <Popconfirm title="새 기기 없이 내리면 그 시각부터 이 지점의 기록이 멈춥니다. 철거할까요?" okText="철거" cancelText="취소" onConfirm={() => submitSwap(true)}>
            <Button danger icon={<StopOutlined />}>철거만</Button></Popconfirm>}
          <Button onClick={() => setSwapFor(null)}>취소</Button>
          <Button type="primary" loading={saving} onClick={() => submitSwap(false)}>{swapFor?.current ? '교체 기록' : '설치 기록'}</Button>
        </Space>}>
        {swapFor && <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={swapFor.current
              ? <span>지금 기기 <b>{swapFor.current.device_name}</b>({swapFor.current.imei}) 을 교체 시각에 내리고 새 기기로 이어 기록합니다.</span>
              : '이 지점에 기기를 설치합니다. 설치 시각부터 기기의 측정값이 이 지점 기록이 됩니다.'}
            description={<span style={{ fontSize: 12 }}>권장 절차: 새 기기를 같은 자리에 <b>2시간 이상 나란히</b> 두고(병행 측정) 차이가 온도 ±0.5℃ · 습도 ±3%RH 안이면 교체 시각을 정해 기록한다.
              차이는 아래 '교체 확인'에 남긴다(GLP 추적성). 옛 기기의 병행 측정값은 기기 기록에 그대로 남는다.</span>} />
          <Form form={swapForm} layout="vertical" size="small">
            <Space size={8} style={{ display: 'flex' }} align="start">
              <Form.Item name="at" label="교체(설치) 시각" rules={[{ required: true }]}><DatePicker showTime={{ format: 'HH:mm' }} format={DATETIME_FORMAT} /></Form.Item>
              {swapFor.current && <Form.Item name="end_reason" label="내리는 사유"><Select style={{ width: 130 }} options={END_REASONS.map(v => ({ value: v, label: v }))} /></Form.Item>}
              <Form.Item name="start_reason" label="설치 사유"><Select style={{ width: 120 }} options={START_REASONS.map(v => ({ value: v, label: v }))} /></Form.Item>
            </Space>
            <Form.Item name="device_id" label={`설치할 기기 (예비기 ${spares.length}대)`}
              extra={spares.length ? '어느 지점에도 설치되지 않은 활성 기기만 보인다' : '예비기가 없다 — 장치 대장에 등록하거나 다른 지점에서 먼저 철거'}>
              <Select showSearch optionFilterProp="label" placeholder="기기 선택"
                options={spares.map(d => ({ value: d.id, label: `${d.name} · ${d.imei}${d.last_battery_v ? ` · ${d.last_battery_v}V` : ''}` }))} />
            </Form.Item>
            <Form.Item name="check_note" label="교체 확인 (병행 측정 · 교정)"><Input placeholder="병행 3h Δ0.2℃ · Δ1.4%RH / 교정성적서 KC-2026-031" /></Form.Item>
            <Form.Item name="note" label="비고"><Input.TextArea rows={2} placeholder="고장 증상 · 조치" /></Form.Item>
          </Form>
        </>}
      </Modal>

      {/* 이력 */}
      <Drawer open={!!histFor} onClose={() => setHistFor(null)} width={520} title={histFor ? `${histFor.code} ${histFor.name} — 설치 · 배정 이력` : ''}>
        {!hist ? <Text type="secondary">불러오는 중…</Text> : <>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>기기 설치 이력</div>
          {hist.devices.length ? (
            <Timeline items={hist.devices.map(x => ({
              color: x.valid_to ? 'gray' : 'green',
              children: (
                <div style={{ fontSize: 12.5 }}>
                  <div><b>{x.device_name}</b> <Text type="secondary" style={{ fontSize: 11 }}>{x.imei}</Text></div>
                  <div style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(x.valid_from)} ~ {x.valid_to ? fmtDateTime(x.valid_to) : <b style={{ color: state.successText }}>현재</b>}</div>
                  <Space size={4} wrap style={{ marginTop: 2 }}>
                    {x.start_reason && <Tag style={{ margin: 0 }}>설치: {x.start_reason}</Tag>}
                    {x.end_reason && <Tag color="orange" style={{ margin: 0 }}>내림: {x.end_reason}</Tag>}
                  </Space>
                  {x.check_note && <div style={{ color: gray[600] }}>확인: {x.check_note}</div>}
                  {x.note && <div style={{ color: gray[500] }}>{x.note}</div>}
                  {canDelete && <Popconfirm title="이 설치 이력을 지울까요? 그 기간의 값이 지점 기록에서 빠집니다." okText="삭제" cancelText="취소"
                    onConfirm={async () => { try { await thloggerAPI.deletePointDevice(x.id); message.success('삭제되었습니다.'); openHist(histFor); reload(); } catch (e) { err(e, '삭제에 실패했습니다.'); } }}>
                    <Button type="link" size="small" danger style={{ padding: 0 }}>이력 삭제</Button></Popconfirm>}
                </div>),
            }))} />
          ) : <EmptyState title="설치 이력이 없습니다" size="sm" />}
          <div style={{ fontWeight: 700, margin: '8px 0' }}>시험 배정</div>
          <Table size="small" pagination={false} rowKey="id" dataSource={hist.assignments}
            locale={{ emptyText: <EmptyState title="배정 이력이 없습니다" size="sm" /> }}
            columns={[
              { title: '시험번호', dataIndex: 'test_number', width: 110 },
              { title: '기간', key: 'p', render: (_: any, a: any) => `${fmtDate(a.valid_from)} ~ ${a.valid_to ? fmtDate(a.valid_to) : '진행 중'}` },
              { title: '', key: 'x', width: 70, render: (_: any, a: any) => canWrite && (
                <Space size={0}>
                  {!a.valid_to && <Tooltip title="오늘로 종료"><Button type="text" size="small" icon={<StopOutlined />} onClick={async () => { await thloggerAPI.closeAssignment(a.id); openHist(histFor); reload(); }} /></Tooltip>}
                  <Popconfirm title="배정을 삭제할까요?" okText="삭제" cancelText="취소" onConfirm={async () => { await thloggerAPI.deleteAssignment(a.id); openHist(histFor); reload(); }}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                </Space>) },
            ]} />
        </>}
      </Drawer>

      {/* 시험 배정 */}
      <Modal open={!!assignFor} title={assignFor ? `시험 배정 — ${assignFor.code} ${assignFor.name}` : ''} onCancel={() => setAssignFor(null)} onOk={submitAssign}
        okText="배정" cancelText="취소" confirmLoading={saving} destroyOnClose width={520}>
        <Text type="secondary" style={{ fontSize: 12 }}>지점에 배정하면 기간 중 기기를 바꿔도 시험의 환경 기록이 이어진다.</Text>
        <Form form={assignForm} layout="vertical" size="small" style={{ marginTop: 8 }}>
          <Form.Item name="schedule_id" label="시험일정">
            <Select showSearch allowClear placeholder="시험번호·항목명 2자 이상" filterOption={false} onSearch={searchSchedules} options={schedOpts} notFoundContent={null} />
          </Form.Item>
          <Form.Item name="test_number" label="또는 시험번호"><Input placeholder="SR26-001" style={{ width: 160 }} /></Form.Item>
          <Space size={8}>
            <Form.Item name="valid_from" label="시작" rules={[{ required: true }]}><DatePicker format={DATE_FORMAT} /></Form.Item>
            <Form.Item name="valid_to" label="종료"><DatePicker format={DATE_FORMAT} placeholder="진행 중" /></Form.Item>
          </Space>
          <Form.Item name="note" label="비고"><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ThLoggerPoints;
